'use client'

import { createContext, useContext, type ComponentType } from 'react'
import { isServer } from '@utils/isServer'
import { observer } from 'mobx-react-lite'

import { PostHogProvider } from '@/shared/analytics/posthog-provider'
import { type ProviderProps } from '../types'
import { AppStore } from './app-store'

export const StoreContext = createContext<AppStore | null>(null)

export function useAppStore() {
  const context = useContext(StoreContext)
  if (context === undefined || context === null) {
    throw new Error('useAppStore must be used within AppStoreProvider')
  }

  return context
}

let store: AppStore

function initializeStore(initialData?: any) {
  const _store = store ?? new AppStore()

  if (initialData) {
    _store.hydrate(initialData).then(() => {})
  }

  if (isServer()) return _store
  store ??= _store

  return _store
}

interface AppStoreProviderProps extends ProviderProps {
  isImpersonating?: boolean
}

interface PostHogIdentityBridgeProps extends Pick<AppStoreProviderProps, 'children' | 'isImpersonating'> {
  store: Pick<AppStore, 'appState'>
}

export const PostHogIdentityBridgeView = ({ children, isImpersonating = false, store }: PostHogIdentityBridgeProps) => (
  <PostHogProvider isImpersonating={isImpersonating} userId={store.appState.user?.id}>
    {children}
  </PostHogProvider>
)

export const PostHogIdentityBridge = observer(PostHogIdentityBridgeView)

PostHogIdentityBridge.displayName = 'PostHogIdentityBridge'

export function AppStoreProvider({
  children,
  initialState: initialData,
  isImpersonating = false,
}: AppStoreProviderProps) {
  const store = initializeStore(initialData)

  return (
    <StoreContext.Provider value={store}>
      <PostHogIdentityBridge isImpersonating={isImpersonating} store={store}>
        {children}
      </PostHogIdentityBridge>
    </StoreContext.Provider>
  )
}

AppStoreProvider.displayName = 'AppStoreProvider'

export function withAppStore<P extends object>(WrappedComponent: ComponentType<P>, initialState?: any) {
  const ComponentWithAppStore = (props: P) => {
    return (
      <AppStoreProvider initialState={initialState}>
        <WrappedComponent {...(props as P)} />
      </AppStoreProvider>
    )
  }

  // Устанавливаем displayName для удобной отладки
  ComponentWithAppStore.displayName = `withAppStore(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`

  return ComponentWithAppStore
}
